// frontend/src/components/parts/PartForm.js
// The "Part details" form from the spec, shared by Add Part and Edit Part.
// Fields: Part Number, Description, Task, Task Type, Engineer, Priority,
// Status, Estimated Hours, Revision, CO Number, Customer, Comments, Attachments.
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Grid, TextField, Button, MenuItem, Typography, Alert, Divider,
    CircularProgress, Chip, IconButton, List, ListItem, ListItemText,
    InputAdornment, Tooltip, Autocomplete,
} from '@mui/material';
import { Save, Cancel, CloudUpload, Delete, AttachFile } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/AuthContext';
import useReferenceData from '../../hooks/useReferenceData';
import {
    createPart, updatePart, uploadPartFiles, getPartFiles, getTicketOptions,
} from '../../services/endpoints';
import { isManagerRole, ROLE_LABELS } from '../../constants/domain';
import RichCommentEditor from '../common/RichCommentEditor';
import { isBlankHtml } from '../../utils/richText';

// Whether this part record is a single component or a multi-part assembly.
const TASK_OPTIONS = ['PART', 'ASSEMBLY'];

const EMPTY = {
    part_number: '',
    part_name: '',
    description: '',
    task: '',
    task_type: 'PSG',
    assigned_to: '',
    qc_engineer: '',
    ticket_id: '',
    priority: 'Medium',
    status: 'Yet to Start',
    estimated_hours: '',
    actual_hours: '',
    revision: '',
    co_number: '',
    customer: '',
    start_date: '',
    due_date: '',
    comments: '',
};

/** Accepts "8", "8.5" or "08:30" and returns decimal hours. */
export function parseHours(value) {
    const s = String(value ?? '').trim();
    if (!s) return null;
    const hhmm = s.match(/^(\d{1,3}):([0-5]?\d)$/);
    if (hhmm) return Number(hhmm[1]) + Number(hhmm[2]) / 60;
    const n = Number(s);
    return isNaN(n) ? null : n;
}

const PartForm = ({ part, onSuccess, onCancel }) => {
    const { user } = useAuth();
    const isEdit = !!part?.id;
    const manager = isManagerRole(user?.role);
    const ref = useReferenceData();

    const [form, setForm] = useState(EMPTY);
    // Actual hours accrue automatically once work starts (see
    // Part.changeStatus) -- there's nothing to show or log before the part
    // has left "Yet to Start", so the field only appears from In Progress on.
    const hideActualHours = form.status === 'Yet to Start';
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState([]);          // newly picked files
    const [existingFiles, setExistingFiles] = useState([]);
    // The linked JIRA ticket -- kept as the full {id, ticket_key, ...} option
    // so the Autocomplete has a label to show, not just the bare id in form.ticket_id.
    const [ticketOption, setTicketOption] = useState(null);
    const [ticketOptions, setTicketOptions] = useState([]);
    const [ticketOptionsLoading, setTicketOptionsLoading] = useState(true);

    // Seed the form when editing.
    useEffect(() => {
        if (!part) {
            setForm(EMPTY);
            setTicketOption(null);
            return;
        }
        setForm({
            ...EMPTY,
            ...Object.fromEntries(
                Object.keys(EMPTY).map(k => [k, part[k] ?? ''])
            ),
            comments: '',   // comments are appended, never pre-filled
        });
        setTicketOption(part.ticket_id
            ? { id: part.ticket_id, ticket_key: part.ticket_key }
            : null);
    }, [part]);

    // Every ticket, loaded once -- the field is a plain dropdown (with
    // type-to-filter) rather than a type-ahead search.
    useEffect(() => {
        getTicketOptions()
            .then(res => setTicketOptions(res.data || []))
            .catch(() => setTicketOptions([]))
            .finally(() => setTicketOptionsLoading(false));
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        getPartFiles(part.id)
            .then(res => setExistingFiles(res.data || []))
            .catch(() => setExistingFiles([]));
    }, [isEdit, part?.id]);

    const set = (field) => (e) => {
        const value = e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.part_number.trim()) e.part_number = 'Part number is required';
        if (!form.part_name.trim()) e.part_name = 'Description is required';
        if (!form.task_type) e.task_type = 'Task type is required';
        if (!form.ticket_id) e.ticket_id = 'JIRA ticket is required';
        if (form.estimated_hours && parseHours(form.estimated_hours) === null) {
            e.estimated_hours = 'Use hours (8.5) or HH:MM (08:30)';
        }
        if (form.actual_hours && parseHours(form.actual_hours) === null) {
            e.actual_hours = 'Use hours (8.5) or HH:MM (08:30)';
        }
        if (form.start_date && form.due_date && form.due_date < form.start_date) {
            e.due_date = 'Target date cannot be before the start date';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) {
            toast.error('Please fix the highlighted fields');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                part_number: form.part_number.trim(),
                part_name: form.part_name.trim(),
                description: form.description.trim() || form.part_name.trim(),
                task: form.task.trim() || null,
                task_type: form.task_type,
                priority: form.priority,
                revision: form.revision.trim() || null,
                co_number: form.co_number.trim() || null,
                customer: form.customer.trim() || null,
                start_date: form.start_date || null,
                due_date: form.due_date || null,
                estimated_hours: parseHours(form.estimated_hours),
                actual_hours: parseHours(form.actual_hours),
                ticket_id: form.ticket_id || null,
            };
            // Anyone can pick the Engineer while creating a part; reassigning
            // an existing part (and setting the QC Engineer) stays manager-only.
            if (!isEdit) {
                payload.assigned_to = form.assigned_to || null;
            } else if (manager) {
                payload.assigned_to = form.assigned_to || null;
                payload.qc_engineer = form.qc_engineer || null;
            }
            // Status is only settable at creation; afterwards it moves through
            // the status-flow endpoint so transitions stay validated.
            if (!isEdit) {
                payload.status = form.status;
                if (!isBlankHtml(form.comments)) payload.comments = form.comments;
            }

            const res = isEdit
                ? await updatePart(part.id, payload)
                : await createPart(payload);
            const saved = res.data;

            if (files.length > 0) {
                try {
                    await uploadPartFiles(saved.id, files);
                } catch (err) {
                    toast.warning(
                        `Part saved, but the attachments failed: ${err.response?.data?.message || err.message}`
                    );
                }
            }

            toast.success(isEdit
                ? `Part ${saved.part_number} updated`
                : `Part ${saved.part_number} created`);
            onSuccess?.(saved);
        } catch (err) {
            const data = err.response?.data;
            if (data?.errors) {
                const mapped = {};
                data.errors.forEach(e2 => { mapped[e2.path || e2.param] = e2.msg; });
                setErrors(mapped);
                toast.error('Please fix the highlighted fields');
            } else {
                toast.error(data?.message || err.message || 'Could not save the part');
            }
        } finally {
            setSaving(false);
        }
    };

    const engineerOptions = useMemo(() => ref.engineers, [ref.engineers]);

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
                {/* --- identity --- */}
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Part Number"
                        value={form.part_number} onChange={set('part_number')}
                        error={!!errors.part_number} helperText={errors.part_number}
                        disabled={isEdit}
                        placeholder="EPT01234" />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Description"
                        value={form.part_name} onChange={set('part_name')}
                        error={!!errors.part_name} helperText={errors.part_name}
                        placeholder="Housing" />
                </Grid>

                <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={2} label="Detailed notes"
                        value={form.description} onChange={set('description')}
                        placeholder="Optional longer description" />
                </Grid>

                {/* --- classification --- */}
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        fullWidth
                        options={ticketOptions}
                        value={ticketOption}
                        getOptionLabel={(o) => o
                            ? `${o.ticket_key}${o.type_of_activity ? ' — ' + o.type_of_activity : ''}`
                            : ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        loading={ticketOptionsLoading}
                        onChange={(e, value) => {
                            setTicketOption(value);
                            setForm(prev => ({ ...prev, ticket_id: value ? value.id : '' }));
                            if (errors.ticket_id) setErrors(prev => ({ ...prev, ticket_id: undefined }));
                        }}
                        renderInput={(params) => (
                            <TextField {...params} required label="JIRA Ticket"
                                placeholder="Choose the ticket this part was created for"
                                error={!!errors.ticket_id}
                                helperText={errors.ticket_id || 'Link the JIRA ticket this part was created for'}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {ticketOptionsLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }} />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField select fullWidth label="Part Type" value={form.task}
                        onChange={set('task')}>
                        {TASK_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField select fullWidth required label="Task Type"
                        value={form.task_type} onChange={set('task_type')}
                        error={!!errors.task_type} helperText={errors.task_type}>
                        {ref.taskTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField select fullWidth label="Priority"
                        value={form.priority} onChange={set('priority')}>
                        {ref.priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField select fullWidth label="Status"
                        value={form.status} onChange={set('status')}
                        disabled={isEdit}
                        helperText={isEdit ? 'Change status from the status flow action' : ' '}>
                        {ref.statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                </Grid>

                {/* --- assignment --- */}
                {/* QC Engineer is only settable once the part exists -- Add Part
                    skips it, the QC reviewer is assigned later from Edit Part. */}
                <Grid item xs={12} sm={isEdit ? 6 : 12}>
                    <TextField select fullWidth label="Engineer"
                        value={form.assigned_to} onChange={set('assigned_to')}
                        disabled={isEdit && !manager}
                        helperText={isEdit && !manager ? 'Only managers can reassign work' : ' '}>
                        <MenuItem value=""><em>Unassigned</em></MenuItem>
                        {engineerOptions.map(u => (
                            <MenuItem key={u.id} value={u.id}>
                                {u.full_name}
                                <Chip label={ROLE_LABELS[u.role] || u.role} size="small"
                                    variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                {isEdit && (
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="QC Engineer"
                            value={form.qc_engineer} onChange={set('qc_engineer')}
                            disabled={!manager}
                            helperText={manager ? 'Reviews at Peer / Final QC' : ' '}>
                            <MenuItem value=""><em>Unassigned</em></MenuItem>
                            {engineerOptions.map(u => (
                                <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                )}

                {/* --- schedule + effort --- */}
                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="date" label="Start date"
                        value={form.start_date ? String(form.start_date).slice(0, 10) : ''}
                        onChange={set('start_date')}
                        InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="date" label="Target date"
                        value={form.due_date ? String(form.due_date).slice(0, 10) : ''}
                        onChange={set('due_date')}
                        error={!!errors.due_date} helperText={errors.due_date}
                        InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Estimated Hours"
                        value={form.estimated_hours} onChange={set('estimated_hours')}
                        error={!!errors.estimated_hours}
                        helperText={errors.estimated_hours || 'e.g. 8.5 or 08:30'}
                        placeholder="00:00"
                        InputProps={{
                            endAdornment: <InputAdornment position="end">h</InputAdornment>,
                        }} />
                </Grid>
                {/* Removed from the form while the part is still "Yet to Start" --
                    no work, no hours yet. It appears once the status flow
                    reaches "In Progress" (and stays visible from there on),
                    since actual hours accrue automatically as the part moves
                    through status changes (the feed-in flow in
                    Part.changeStatus). */}
                {!hideActualHours && (
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth label="Actual Hours"
                            value={form.actual_hours} onChange={set('actual_hours')}
                            disabled={isEdit && !manager}
                            error={!!errors.actual_hours}
                            helperText={errors.actual_hours || (isEdit && !manager
                                ? 'Logged automatically as the part moves through each stage'
                                : 'Logged so far')}
                            placeholder="00:00"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">h</InputAdornment>,
                            }} />
                    </Grid>
                )}

                {/* --- references --- */}
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Revision (optional)"
                        value={form.revision} onChange={set('revision')} placeholder="A" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="CO Number (optional)"
                        value={form.co_number} onChange={set('co_number')} placeholder="CO-1234" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Customer"
                        value={form.customer} onChange={set('customer')} />
                </Grid>

                {/* --- comments (create only) --- */}
                {!isEdit && (
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>Comments</Typography>
                        <RichCommentEditor
                            value={form.comments}
                            onChange={(html) => setForm(f => ({ ...f, comments: html }))}
                            placeholder="Initial notes for this part" />
                    </Grid>
                )}

                {/* --- attachments --- */}
                <Grid item xs={12}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="subtitle2" gutterBottom>
                        Attachments
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        PDF, STEP, SolidWorks (.sldprt/.slddrw), Excel, DWG, images. Max 100 MB each.
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                        <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                            Choose files
                            <input hidden multiple type="file"
                                onChange={(e) => setFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                        </Button>
                    </Box>

                    {files.length > 0 && (
                        <List dense>
                            {files.map((f, i) => (
                                <ListItem key={`${f.name}-${i}`} disableGutters
                                    secondaryAction={
                                        <IconButton size="small"
                                            onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    }>
                                    <AttachFile fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <ListItemText
                                        primary={f.name}
                                        secondary={`${(f.size / 1024 / 1024).toFixed(2)} MB`} />
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {existingFiles.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Already attached
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {existingFiles.map(f => (
                                    <Tooltip key={f.id} title={`Uploaded by ${f.uploaded_by_name || 'unknown'}`}>
                                        <Chip
                                            label={f.file_name}
                                            size="small"
                                            component="a"
                                            clickable
                                            icon={<AttachFile />}
                                            href={`${(process.env.REACT_APP_SOCKET_URL || '')}${f.file_path}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {isEdit && !manager && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    As an engineer you can add and update parts, but reassigning an
                    existing part is managed by your manager.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={onCancel} startIcon={<Cancel />} disabled={saving}>
                    Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
                    {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create part'}
                </Button>
            </Box>
        </Box>
    );
};

export default PartForm;
